// Google Analytics utility for tracking user interactions and conversions
// Uses gtag.js already loaded in index.html

import { hasAnalyticsConsent } from "@/components/CookieBanner";

declare global {
  interface Window {
    gtag?: (command: string, ...args: any[]) => void;
    dataLayer?: any[];
  }
}

class Analytics {
  private gtagChecked = false;

  // Check if gtag is available dynamically (not just at construction)
  private isGtagAvailable(): boolean {
    const available = typeof window !== 'undefined' && typeof window.gtag === 'function';

    // Log once when gtag becomes available
    if (available && !this.gtagChecked && process.env.NODE_ENV === 'development') {
      console.log('✅ Google Analytics (gtag) is ready');
      this.gtagChecked = true;
    }

    return available;
  }

  // Analytics toujours actif (pas de gating par cookie consent)
  private hasConsent(): boolean {
    return true;
  }

  // Track custom events
  track(eventName: string, parameters: Record<string, any> = {}) {
    if (!this.isGtagAvailable()) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ GA not loaded yet, skipping event:', eventName);
      }
      return;
    }

    // Check cookie consent before tracking
    if (!this.hasConsent()) {
      if (process.env.NODE_ENV === 'development') {
        console.log('🍪 Analytics blocked (no consent):', eventName);
      }
      return;
    }

    window.gtag('event', eventName, {
      ...parameters,
      page_path: window.location.pathname,
      page_title: document.title,
      timestamp: new Date().toISOString()
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 GA Event:', eventName, parameters);
    }
  }

  // Track page views
  pageView(path?: string) {
    if (!this.isGtagAvailable()) return;

    window.gtag('event', 'page_view', {
      page_path: path || window.location.pathname,
      page_title: document.title,
      page_location: window.location.href
    });
  }

  // Track form interactions
  formStart(formType: string) {
    this.track('form_start', {
      form_type: formType,
      form_location: window.location.pathname
    });
  }

  formComplete(formType: string, duration?: number) {
    this.track('form_submit', {
      form_type: formType,
      form_duration: duration,
      form_location: window.location.pathname
    });
  }

  formError(formType: string, error: string) {
    this.track('form_error', {
      form_type: formType,
      error_message: error,
      form_location: window.location.pathname
    });
  }

  // Track CTA clicks
  ctaClick(ctaName: string, ctaLocation: string) {
    this.track('cta_click', {
      cta_name: ctaName,
      cta_location: ctaLocation
    });
  }

  // Track conversions/leads
  leadGenerated(formType: string, leadValue?: number) {
    this.track('generate_lead', {
      form_type: formType,
      value: leadValue || 0,
      currency: 'EUR'
    });
  }

  // Track scroll depth
  scrollDepth(percentage: number) {
    this.track('scroll', {
      percent_scrolled: percentage
    });
  }

  // Track time on page
  timeOnPage(seconds: number) {
    this.track('timing_complete', {
      name: 'page_engagement',
      value: seconds,
      event_category: 'engagement'
    });
  }

  // Track errors
  error(error: Error, context?: string) {
    this.track('exception', {
      description: error.message,
      fatal: false,
      context: context
    });
  }

  // Track file downloads
  downloadFile(fileName: string, fileType: string) {
    this.track('file_download', {
      file_name: fileName,
      file_extension: fileType,
      link_url: window.location.href
    });
  }

  // Track FAQ interactions
  faqOpen(question: string, index: number) {
    this.track('faq_open', {
      event_category: 'engagement',
      question_text: question.substring(0, 100),
      question_index: index
    });
  }

  // Track RDV booking attempts
  rdvBooking(source: string) {
    this.track('rdv_booking', {
      event_category: 'conversion',
      booking_source: source
    });
  }

  // Track testimonial views
  testimonialView(clientName: string, sector: string) {
    this.track('testimonial_view', {
      event_category: 'social_proof',
      client_name: clientName,
      client_sector: sector
    });
  }

  // Track article views
  articleView(slug: string, category: string, readTime: number) {
    this.track('article_view', {
      event_category: 'content',
      article_slug: slug,
      article_category: category,
      read_time: readTime
    });
  }

  // Track phone call clicks
  phoneClick(phoneNumber: string, source: string) {
    this.track('phone_click', {
      event_category: 'conversion',
      phone_number: phoneNumber,
      click_source: source
    });
  }

  // Track simulator usage
  simulatorUse(simulatorName: string, resultValue?: number) {
    this.track('simulator_use', {
      event_category: 'engagement',
      simulator_name: simulatorName,
      value: resultValue
    });
  }

  // Track outbound link clicks
  outboundClick(url: string, linkText?: string) {
    this.track('outbound_click', {
      event_category: 'navigation',
      outbound_url: url,
      link_text: linkText
    });
  }

  // Check if analytics is working
  isEnabled(): boolean {
    return this.isGtagAvailable();
  }
}

// Singleton instance
export const analytics = new Analytics();

// React hook for analytics
export function useAnalytics() {
  return {
    track: analytics.track.bind(analytics),
    pageView: analytics.pageView.bind(analytics),
    formStart: analytics.formStart.bind(analytics),
    formComplete: analytics.formComplete.bind(analytics),
    formError: analytics.formError.bind(analytics),
    error: analytics.error.bind(analytics),
    leadGenerated: analytics.leadGenerated.bind(analytics),
    ctaClick: analytics.ctaClick.bind(analytics),
    scrollDepth: analytics.scrollDepth.bind(analytics),
    timeOnPage: analytics.timeOnPage.bind(analytics),
    downloadFile: analytics.downloadFile.bind(analytics),
    // New tracking methods
    faqOpen: analytics.faqOpen.bind(analytics),
    rdvBooking: analytics.rdvBooking.bind(analytics),
    testimonialView: analytics.testimonialView.bind(analytics),
    articleView: analytics.articleView.bind(analytics),
    phoneClick: analytics.phoneClick.bind(analytics),
    simulatorUse: analytics.simulatorUse.bind(analytics),
    outboundClick: analytics.outboundClick.bind(analytics),
    isEnabled: analytics.isEnabled.bind(analytics)
  };
}